import { faArrowUp, faCopy } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useSelector } from "react-redux";

const exampleMessages = [
  { id: 1, message: "I ran a red light and caused an accident." },
  {
    id: 2,
    message:
      "I ran over someone with my car because I was drunk. What consequences do I face?",
  },
  {
    id: 3,
    message: "I stole a watch from a store. What consequences do I face?",
  },
  {
    id: 4,
    message:
      "My house was broken into, and I killed the burglar. What consequences do I face?",
  },
  {
    id: 5,
    message:
      "What is the penalty for driving 30 km/h over the speed limit in an urban area?",
  },
  { id: 6, message: "I smashed a window. What consequences do I face?" },
];

const ChatWindow = ({ messages, onSendMessage, isNewChat, mode }) => {
  const [input, setInput] = useState("");
  const [chatHistory, setChatHistory] = useState(messages);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [documentDetails, setDocumentDetails] = useState({
    type: "",
    parties: "",
    date: "",
    terms: "",
  });

  const messagesEndRef = useRef(null);
  const isAuthenticated = useSelector((state) => state.theme.isAuthenticated);

  useEffect(() => {
    if (mode === "Legal Document" && step === 0) {
      setStep(1);
      askNextQuestion(1);
    }
  }, [mode]);

  useEffect(() => {
    setChatHistory(messages);
  }, [messages]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatHistory, loading]);

  const askNextQuestion = (nextStep) => {
    let question = "";
    if (nextStep === 1)
      question = "What type of legal document do you want to generate?";
    if (nextStep === 2)
      question = "Who are the parties involved in this document?";
    if (nextStep === 3)
      question = "What is the date or time period relevant to this document?";
    if (nextStep === 4)
      question = "Do you have any specific terms or details to include?";

    if (nextStep <= 4) {
      setTimeout(() => {
        setChatHistory((prev) => [
          ...prev,
          { role: "assistant", content: question },
        ]);
      }, 2000);
    }
  };

  const handleSubmit = async () => {
    if (!input.trim()) return;

    const userMessage = { role: "user", content: input.trim() };
    setChatHistory((prev) => [...prev, userMessage]);
    const userResponse = input.trim();
    setInput("");

    if (mode === "Legal Document") {
      handleLegalDocumentSteps(userResponse);
      return;
    }

    setLoading(true);
    try {
      const botResponse = await onSendMessage(userResponse);
      if (!isAuthenticated) {
        setChatHistory((prev) => [
          ...prev,
          { role: "assistant", content: "Please log in to use this feature." },
        ]);
        return;
      }
      // setChatHistory((prev) => [
      //   ...prev,
      //   { role: "assistant", content: botResponse },
      // ]);
    } catch (error) {
      setChatHistory((prev) => [
        ...prev,
        { role: "assistant", content: "Error getting response." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleLegalDocumentSteps = (response) => {
    const updatedDetails = { ...documentDetails };

    if (step === 1) updatedDetails.type = response;
    if (step === 2) updatedDetails.parties = response;
    if (step === 3) updatedDetails.date = response;
    if (step === 4) updatedDetails.terms = response;

    setDocumentDetails(updatedDetails);

    if (step < 4) {
      const nextStep = step + 1;
      setStep(nextStep);
      askNextQuestion(nextStep);
    } else {
      submitDocumentDetails(updatedDetails);
    }
  };

  const handleExampleClick = (example) => {
    setInput(example);
    // handleSubmit();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const submitDocumentDetails = async (details) => {
    setLoading(true);
    setChatHistory((prev) => [
      ...prev,
      { role: "assistant", content: "Generating your legal document..." },
    ]);

    try {
      // Convert object to a readable string
      const detailsString = `
        Type: ${details.type}
        Parties: ${details.parties}
        Date: ${details.date}
        Terms: ${details.terms}
      `;

      const response = await onSendMessage(detailsString); // Sending as string
      // console.log("response", response);
      // setChatHistory((prev) => [
      //   ...prev,
      //   { role: "assistant", content: `Your document is ready:\n\n${response}` },
      // ]);
    } catch (error) {
      setChatHistory((prev) => [
        ...prev,
        { role: "assistant", content: "Error generating document." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  console.log("chatHistory", chatHistory);

  if (mode === "Legal Document") {
    return (
      <div className="container chat-window document-generation p-4">
        <div className="messages-container p-4 flex-grow-1 overflow-auto">
          {chatHistory.map((m, i) => (
            <div
              key={i}
              className={`message ${
                m.role === "user" ? "user-message" : "bot-message"
              }`}
            >
              <span>{m.content}</span>
            </div>
          ))}

          {loading && (
            <div className="message bot-message">
              <strong>Bot:</strong>{" "}
              <span className="loading-dots">Typing...</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="input-area mx-auto shadow mt-3 d-flex align-items-center">
          <textarea
            className="border-0 textfield"
            placeholder="Type your response here"
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            style={{ resize: "none" }}
          />
          <button
            className="btn btn-dark p-3 send-button"
            onClick={handleSubmit}
            disabled={loading}
          >
            <FontAwesomeIcon size="xl" icon={faArrowUp} />
          </button>
        </div>

        <div className="small text-muted mt-4 text-center">
          Big Barrister Chatbot may not always be accurate. Please check
          important information.
        </div>
      </div>
    );
  }

  return (
    <div className="container chat-window d-flex flex-column justify-content-between">
      {chatHistory.length === 0 && isNewChat ? (
        <div className="text-center w-100 mt-lg-5 mt-3 messages-container">
          <h3 className="mb-0">What can I help with?</h3>
          <h5 className="mb-0">For example, you can ask me:</h5>
          <div className="d-flex flex-column flex-md-row flex-wrap mb-lg-5 mb-3 justify-content-center example-questions">
            {exampleMessages.map((msg) => (
              <button
                key={msg.id}
                className="example-question-button shadow me-0 me-lg-4 py-3"
                onClick={() => handleExampleClick(msg.message)}
              >
                {msg.message}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="messages-container p-lg-5 flex-grow-1 overflow-auto">
          {chatHistory.map((m, i) => (
            <div
              key={i}
              className={`message ${
                m.role === "user" ? "user-message" : "bot-message"
              }`}
            >
              {m.role === "system" ? (
                <div className="bot-response">
                  <ReactMarkdown>{m.content}</ReactMarkdown>
                  <button
                    className="copy-btn text-end"
                    onClick={() => copyToClipboard(m.content)}
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </button>
                </div>
              ) : (
                <span>{m.content}</span>
              )}
            </div>
          ))}

          {loading && (
            <div className="message bot-message">
              <strong>Bot:</strong>{" "}
              <span className="loading-dots">Typing...</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      )}

      <div>
        <div className="input-area mx-auto shadow mt-3 d-flex align-items-center">
          <textarea
            className="border-0 textfield"
            placeholder="Ask anything"
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            style={{ resize: "none" }}
          />
          <button
            className="btn btn-dark p-3 send-button"
            onClick={handleSubmit}
            disabled={loading}
          >
            <FontAwesomeIcon size="xl" icon={faArrowUp} />
          </button>
        </div>

        <div className="small text-muted mt-1 mt-lg-4 text-center bot-instructions">
          Big Barrister Chatbot may not always be accurate. Please check
          important information.
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
